import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import Table from "../../CommonComponents/Table";
import { StatusBadge } from "../MockPage/MockTest/MockTestList";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import ReactQuill from "react-quill";
import { LibraryContext } from "../../../context/LibraryContext";
import {
  libTypeIds,
  QUESTION_BASED_TYPES,
  RESULT_STATUS,
} from "../../../constants";
import useAuth from "../../../Hooks/useAuth";
import { useInfiniteScroll } from "../../../Hooks/useInfiniteScroll";
import { useDebouncedValue } from "../../../Hooks/useDebounceValue";
import { getContentType } from "../../../util";
import { DropdownMenu } from "../../CommonComponents/InputDropdown";
import Modal from "../../CommonComponents/Modal";
import ModalBody from "../../CommonComponents/ModalBody";
import QnActivateImg from "../../../assets/images/QnActivate.png";
import QnDeactivateImg from "../../../assets/images/QnDeactivate.png";
import Button from "../../Button";
import ReusableBreadcrumbs from "../../CommonComponents/ReusableBreadCrumbs";

const STATUS_MAP = {
  1: "Active",
  0: "Inactive",
};

const RowActionMenu = ({ row, openMenuId, onToggle, onAction }) => {
  const anchorRef = useRef(null);
  const dropdownId = `row-menu-${row.id}`;
  const isOpen = openMenuId === row.id;

  const [searchParams] = useSearchParams();
  const selectable = searchParams.get("selectable") === "true" || false;

  return (
    <div className="library-question-table-status-column" ref={anchorRef}>
      <StatusBadge value={row.active} statusMap={STATUS_MAP} />
      {!selectable ? (
        <>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(isOpen ? null : row.id);
            }}
            className="library-question-table-status-column-more-icon"
          >
            <path
              d="M10.0013 10.8333C10.4615 10.8333 10.8346 10.4602 10.8346 9.99992C10.8346 9.53968 10.4615 9.16659 10.0013 9.16659C9.54106 9.16659 9.16797 9.53968 9.16797 9.99992C9.16797 10.4602 9.54106 10.8333 10.0013 10.8333Z"
              stroke="#344054"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.0013 4.99992C10.4615 4.99992 10.8346 4.62682 10.8346 4.16659C10.8346 3.70635 10.4615 3.33325 10.0013 3.33325C9.54106 3.33325 9.16797 3.70635 9.16797 4.16659C9.16797 4.62682 9.54106 4.99992 10.0013 4.99992Z"
              stroke="#344054"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.0013 16.6666C10.4615 16.6666 10.8346 16.2935 10.8346 15.8333C10.8346 15.373 10.4615 14.9999 10.0013 14.9999C9.54106 14.9999 9.16797 15.373 9.16797 15.8333C9.16797 16.2935 9.54106 16.6666 10.0013 16.6666Z"
              stroke="#344054"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {isOpen && (
            <DropdownMenu
              open={isOpen}
              anchorRef={anchorRef}
              dropdownId={dropdownId}
              options={[
                { label: "Edit", value: "edit" },
                {
                  label: row.active ? "Deactivate" : "Activate",
                  value: "delete",
                },
              ]}
              onSelectOption={(val) => {
                onAction(val, row);
              }}
              value={null}
              multiple={false}
              onClose={() => onToggle(null)}
            />
          )}
        </>
      ) : null}
    </div>
  );
};

const QuestionList = (props) => {
  const axios = useAxiosPrivate();
  const { type } = useParams();
  const {
    setShowNotify,
    navigate,
    setIsFixedQn,
    setselectque,
    setisqueReport,
    setIsLoaded,
    isLoaded,
  } = useAuth();
  const {
    topics,
    searchText,
    ccTestData,
    setCcTestData,
    selectedSubject,
  } = useContext(LibraryContext);
  const { pathname } = useLocation();

  const [searchParams] = useSearchParams();
  const selectable = searchParams.get("selectable") === "true" || false;
  // const subId = searchParams.get("subId");
  const topId = searchParams.get("topId");
  const complexityId = searchParams.get("complexityid") || "";
  const isSubjectPage = pathname.includes("/subjects");
  const isContentOrAssignment = !QUESTION_BASED_TYPES.includes(+type);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [sortQuestion, setSortQuestion] = useState({});

  const debouncedSearchText = useDebouncedValue(searchText, 500);
  // console.log("topics", topics);

  const selectedTopic = useMemo(() => {
    let topic = topics.find((top) => top.tid === +topId) ?? null;
    if (topic) {
      return topic ?? null;
    }
    return null;
  }, [topId, topics]);

  const handleMenuAction = useCallback(
    async (action, row) => {
      if (action === "edit") {
        await onRowClick(type, row);
      } else if (action === "delete") {
        setEditRow(row);
        setOpenDeleteModal(true);
        setOpenMenuId(null);
      }
    },
    [type],
  );

  const fetchQuestions = useCallback(
    async ({ page, pageSize }) => {
      try {
        const { data } = await axios.get("node/admin/library", {
          params: {
            type,
            pageNumber: page,
            topicId: topId || "",
            complexityid: complexityId,
            searchText: debouncedSearchText ?? "",
            sortBy: sortQuestion.key,
            sortOrder: sortQuestion.direction,
          },
          headers: { "Content-type": "application/json" },
        });
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          const resdata =
            typeof data.data === "string" ? JSON.parse(data.data) : data.data;
          const transFormedData = resdata.map((item, index) => ({
            ...item,
            level: item.topics
              ?.map((topic) => topic.complexityname)
              .filter(Boolean)
              .join(", "),
            sno: (page - 1) * pageSize + index + 1,
          }));
          return { data: transFormedData, total: data.count };
        } else
          throw new Error(
            "Unable to fetch the question list. Please try again later.",
          );
      } catch (error) {
        let isUnAuth = error?.response?.status === 403;
        if (error?.name === "CanceledError" || error?.name === "AbortError")
          return;
        setShowNotify({
          show: true,
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : "Unable to fetch the subjects. Please try again later",
          title: isUnAuth ? "Warning !" : "Error !",
          isUnAuth,
        });
        throw new Error(error);
      }
    },
    [
      axios,
      type,
      topId,
      complexityId,
      setShowNotify,
      debouncedSearchText,
      sortQuestion,
    ],
  );

  const {
    containerRef,
    sentinelRef,
    loading,
    hasMore,
    items: questions,
    setItems: setQuestions,
  } = useInfiniteScroll({
    fetchFn: fetchQuestions,
    pageSize: 10,
    resetKey: JSON.stringify({
      topId,
      complexityId,
      debouncedSearchText,
      type,
      sortQuestion,
    }),
  });

  const onRowClick = async (type, row) => {
    if (+type === libTypeIds.SQL) {
      try {
        const { data } = await axios.get(`node/admin/library/sql/${row.id}`, {
          headers: { "Content-Type": "application/json" },
        });
        let newData = data.data[0];
        newData.qnId = row.id;
        newData.published = 0;
        setIsFixedQn(row.isFixedqn);
        props.que(newData);
        setselectque(newData);
        setisqueReport(true);
        navigate(
          `/library/${type}/questions/${row.id}?fixedqns=${row.isFixedqn}`,
        );
      } catch (error) {
        let isUnAuth = error?.response?.status === 403;
        setShowNotify({
          show: true,
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : "Unable to fetch the question details. Please try again later",
          title: isUnAuth ? "Warning !" : "Error !",
          isUnAuth,
        });
      } finally {
        setIsLoaded(false);
      }
    } else if (+type === libTypeIds.ASSIGNMENT) {
      navigate(`/library/${type}/assignment/${row.id}`);
    } else if (+type === libTypeIds.CONTENT) {
      const contentType = getContentType(row.description);
      if (contentType === "editor") {
        navigate(`/library/${type}/contententry/${row.id}`);
      } else {
        navigate(`/library/${type}/questions/${row.id}`);
      }
    } else {
      navigate(
        `/library/${type}/questions/${row.id}?fixedqns=${row.isFixedqn}`,
      );
    }
  };

  const onChangeStatus = useCallback(
    async (row) => {
      if (row.isFixedqn && row.active) {
        setShowNotify({
          show: true,
          title: "Info",
          msg: "This question has already been assigned as a fixed question, so you can't make it inactive.",
        });
        setOpenDeleteModal(false);
        setEditRow(null);
        return;
      }

      try {
        setIsLoaded(true);
        let libtypeId =
          row.typeid === libTypeIds.SAQ || row.typeid === libTypeIds.LAQ
            ? libTypeIds.DESCRIPTIVE
            : row.typeid === libTypeIds.MCQ_SINGLE ||
              row.typeid === libTypeIds.MCQ_MULTI
            ? libTypeIds.MCQ
            : row.typeid;

        const requestData = {
          id: row.id,
          active: !row.active,
          libtype: libtypeId,
        };

        const { data } = await axios.post(
          "node/admin/question/status",
          JSON.stringify(requestData),
          { headers: { "Content-Type": "application/json" } },
        );
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          setQuestions((pre) => {
            let question = pre.find((qn) => qn.id === row.id);
            if (question) question.active = Number(!question.active);
            return pre;
          });
        } else
          throw new Error(
            "Unable to change the question status. Please try again later",
          );
      } catch (error) {
        let isUnAuth = error?.response?.status === 403;
        setShowNotify({
          show: true,
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : "Unable to change the question status. Please try again later",
          title: isUnAuth ? "Warning !" : "Error !",
          isUnAuth,
        });
      } finally {
        setIsLoaded(false);
        setOpenDeleteModal(false);
        setEditRow(null);
      }
    },
    [axios, setShowNotify, setIsLoaded, setQuestions],
  );

  const renderStatusCol = useCallback(
    (col, row) => (
      <RowActionMenu
        row={row}
        openMenuId={openMenuId}
        onToggle={setOpenMenuId}
        onAction={handleMenuAction}
      />
    ),
    [openMenuId, handleMenuAction],
  );

  const TABLE_COLUMNS = useMemo(
    () => [
      { key: "sno", title: "S.No" },
      {
        key: "name",
        title: QUESTION_BASED_TYPES.includes(+type) ? "QUESTION" : "NAME",
        render: (col, row) => (
          <div className="library-question-table-name-column">
            <ReactQuill readOnly={true} value={col} />

            {row?.title && (
              <p className="library-question-table-name-column-title">
                {row.title}
              </p>
            )}
          </div>
        ),
        sortable: true,
      },
      {
        key: QUESTION_BASED_TYPES.includes(+type) ? "topics" : "topicname",
        title: "TOPIC",
        render: (col) =>
          QUESTION_BASED_TYPES.includes(+type)
            ? col?.map((item) => item.topicname).join(", ")
            : col,
      },
      ...(QUESTION_BASED_TYPES.includes(+type)
        ? [
            {
              key: "level",
              title: "LEVEL",
              render: (col) =>
                Array.isArray(col)
                  ? col
                      .map((item) =>
                        typeof item === "string" ? item : item?.complexityname,
                      )
                      .filter(Boolean)
                      .join(", ")
                  : col,
            },
          ]
        : []),
      { key: "createdby", title: "CREATED BY", sortable: true},
      {
        key: "active",
        title: "STATUS",
        render: renderStatusCol,
      },
    ],
    [renderStatusCol, type],
  );

  const onSelectQuestion = (questionIds = []) => {
    const question = questions.find((qn) => qn.id === questionIds[0]);
    if (question)
      setCcTestData({
        qnId: question.id || null,
        topicId: question.topics[0]?.topicid,
        ccWithFixedQn: true,
      });
  };

  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setEditRow(null);
  };

  // const onBackToTopics = () => {
  //   setSearchParams({ subId });
  //   setSearchText("");
  // };

  const breadcrumbItems = [
    {
      label: "Subject",
      href: `/library/${type}/subjects`,
    },
    {
      label: `${selectedSubject.subName}`,
      href: `/library/${type}/subjects?subId=${selectedSubject.subId}`,
    },
    {
      label: `${selectedTopic?.tname}`,
    },
  ];

  // console.log("selectedSubject:",selectedSubject);

  return (
    <>
      {isSubjectPage && (
        // <div
        //   className="library-question-list-back-btn-container"
        //   onClick={() => onBackToTopics()}
        // >
        //   <svg
        //     width="20"
        //     height="20"
        //     viewBox="0 0 20 20"
        //     fill="none"
        //     xmlns="http://www.w3.org/2000/svg"
        //     className="library-question-list-back-btn"
        //   >
        //     <path
        //       d="M12.5 15L7.5 10L12.5 5"
        //       stroke="#667085"
        //       strokeWidth="2"
        //       strokeLinecap="round"
        //       strokeLinejoin="round"
        //     />
        //   </svg>
        //   {selectedTopic?.tname}
        // </div>
        <ReusableBreadcrumbs items={breadcrumbItems} />
      )}
      <Table
        ref={containerRef}
        className={`library-question-table${
          isContentOrAssignment ? " library-conOrAssi-table" : ""
        }`}
        columns={TABLE_COLUMNS}
        data={questions}
        bordered
        stripped={false}
        fixedHeader
        infiniteScroll
        selectable={selectable}
        sentinelRef={sentinelRef}
        loadingMore={loading}
        hasMore={hasMore}
        selectedRows={[ccTestData.qnId]}
        isMultiSelect={false}
        emptyText={`No ${
          +type === libTypeIds.ASSIGNMENT
            ? "assignments"
            : +type === libTypeIds.CONTENT
            ? "contents"
            : "questions"
        } available`}
        onRowSelect={onSelectQuestion}
        sort={sortQuestion}
        onSortChange={setSortQuestion}
      />
      <Modal
        isOpen={openDeleteModal}
        onClose={onCloseDeleteModal}
        showCloseIcon={false}
        className="library-question-delete-confirm-modal"
      >
        <ModalBody
          content={
            <>
              <img
                src={editRow?.active ? QnDeactivateImg : QnActivateImg}
                alt={`${
                  editRow?.active ? "Deactivate image" : "Activate image"
                }`}
                width={165}
                height={135}
              />
              <div className="library-question-delete-qn">
                Are you sure you want to{" "}
                {editRow?.active ? "deactivate" : "activate"} this question?
                <span className="library-question-delete-refer-text">
                  {editRow?.active
                    ? "This question will be hidden from assessments and learners."
                    : "This question will become available for assessments and visible to learners."}
                </span>
              </div>
            </>
          }
          footer={
            <>
              <Button
                size="sm"
                hierarchy={{ type: "secondaryGrey", buttonText: "Cancel" }}
                onClick={onCloseDeleteModal}
              />
              <Button
                size="sm"
                hierarchy={{
                  type: "primary",
                  buttonText: editRow?.active ? "Deactivate" : "Activate",
                }}
                onClick={() => onChangeStatus(editRow)}
                disable={isLoaded}
              />
            </>
          }
        />
      </Modal>
    </>
  );
};

export default QuestionList;
